<template>
  <button type="button"
          class="relative w-full cursor-pointer pl-6 pr-5 hover:bg-primary-light focus:outline-none focus:bg-primary-light border-none"
          :class="isSelected ? 'text-primary-focus bg-primary-light' : 'text-gray-dark bg-white'"
          @click="$emit('click')"
  >
    <div
      class="flex justify-between items-center py-5 border-t-0 border-l-0 border-r-0 border-b border-solid border-gray-a10"
      @click="handleFilterItem"
    >
      <div class="flex flex-col text-left">
        <span class="text-sm">{{ label }}</span>
        <div class="h-8 mt-2 text-base">
          <list-filter-value v-if="type === FILTER_ITEMS.BOT && useLogicalBots"
                             :labels="botLabels"
                             label-empty="No bots selected"
                             label-full="All bots"
                             :options="logicalBotOptions"
                             class="gap-2"
          >
            <template>
              <bot-icon />
            </template>
          </list-filter-value>

          <filter-value v-if="type === FILTER_ITEMS.BOT && !useLogicalBots"
                        :label="values[FILTER_ITEMS.BOT] ? values[FILTER_ITEMS.BOT].name : ''"
                        :label-classes="'w-56 truncate'"
          >
            <bot-icon/>
          </filter-value>
          <date-from-filter-value v-else-if="type === FILTER_ITEMS.DATE_FROM" :date="values[FILTER_ITEMS.DATE_FROM]"/>
          <date-to-filter-value v-else-if="type === FILTER_ITEMS.DATE_TO" :date="values[FILTER_ITEMS.DATE_TO]"/>
          <filter-value v-if="type === FILTER_ITEMS.GRANULARITY"
                        :label="granularityLabel"
          >
            <granularity-icon/>
          </filter-value>

          <list-filter-value v-if="type === FILTER_ITEMS.MARKETS"
                             :labels="marketLabels"
                             label-empty="No markets selected"
                             label-full="All markets"
                             :options="marketOptions"
                             class="gap-2"
          >
            <template>
              <vs-icon icon="storefront" size="1.3rem"/>
            </template>
          </list-filter-value>

          <list-filter-value v-if="type === FILTER_ITEMS.REQUEST_OUTCOME"
                             :labels="outcomesLabels"
                             label-empty="No outcome selected"
                             label-full="All outcomes"
                             :options="outcomeOptions"
                             :hide-labels="hideOutcomeLabels"
                             class="gap-2"
          >
            <template v-if="checkedOutcomes.length !== outcomeOptions.length">
              <chart-legend v-for="outcome in checkedOutcomes"
                            :key="outcome"
                            :type="outcome"
                            tooltip-position="bottom"
                            :show-label="false"
                            class="h-4 w-4"
              />
            </template>
          </list-filter-value>

          <list-filter-value v-if="type === FILTER_ITEMS.ASSISTANCE_RESOLUTIONS"
                             :labels="resolutionLabels"
                             label-empty="No resolution selected"
                             label-full="All resolutions"
                             :options="resolutionOptions"
                             :hide-labels="hideResolutionLabels"
                             class="gap-2"
          >
            <template v-if="checkedResolutions.length !== resolutionOptions.length">
              <chart-legend v-for="resolution in checkedResolutions"
                            :key="resolution"
                            :type="resolution"
                            tooltip-position="bottom"
                            :show-label="false"
                            class="h-4 w-4"
              />
            </template>
          </list-filter-value>

          <list-filter-value v-if="type === FILTER_ITEMS.CHANNELS"
                             :labels="channelLabels"
                             label-empty="No channels selected"
                             label-full="All channels"
                             :options="channelOptions"
                             :hide-labels="hideChannelLabels"
                             :class="{ 'gap-2': selectedChannels.length !== channelOptions.length && selectedChannels.length }"
          >
            <template>
              <channels-icon v-if="!selectedChannels.length || selectedChannels.length === channelOptions.length"
                             class="mb-1 mr-2"
              />
              <channel-type-icons v-if="selectedChannels.length !== channelOptions.length"
                                  :selectedChannels="selectedChannels"
                                  :hasTooltip="hideChannelLabels"
              />
            </template>
          </list-filter-value>

          <filter-value v-if="type === FILTER_ITEMS.ANSWER_ID"
                        :label="answerProps.name"
          >
            <vs-icon icon="messages" size="1.2rem" class="mt-0.5"/>
          </filter-value>
        </div>
      </div>
      <vs-icon icon="keyboard_arrow_right" size="1.2rem"/>
    </div>

    <div v-if="isSelected"
         v-show="showItemDropdown"
         class="bg-white dropdown absolute z-10 rounded border border-solid border-gray-a10 shadow-lg">
      <div v-if="type === FILTER_ITEMS.BOT && useLogicalBots"
           class="filter-menu-element logical-bot-dropdown text-base"
      >
        <search-input v-if="showLogicalBotSearch"
                      :value="logicalBotSearch"
                      class="px-3 pt-3"
                      @input="logicalBotSearch = $event"
        />

        <div class="px-3 pb-3" :class="showLogicalBotSearch ? 'pt-2' : 'pt-3'">
          <div class="border border-solid border-gray-a10 rounded max-h-84 overflow-auto">
            <checkbox-dropdown v-if="filteredLogicalBotOptions.length"
                               :checked-values="checkedLogicalBots"
                               :options="filteredLogicalBotOptions"
                               :is-no-value-allowed="true"
                               :hide-footer="true"
                               @change="onLogicalBotsChanged"
            />
            <p v-else class="text-sm text-gray-dark text-center py-6 m-0">No bots match your search.</p>
          </div>
        </div>
      </div>

      <radio-dropdown v-else-if="type === FILTER_ITEMS.BOT"
                      :options="botOptions"
                      :value="values[FILTER_ITEMS.BOT].id"
                      class="filter-menu-element w-max text-base"
                      @change="onValueChanged(FILTER_ITEMS.BOT, botOptions.find(({id}) => $event === id))"
      />

      <base-date-picker v-else-if="type === FILTER_ITEMS.DATE_FROM"
                        :clearable="false"
                        :value="values[FILTER_ITEMS.DATE_FROM]"
                        :showDaysForOtherMonth="false"
                        @change-date="onValueChanged(FILTER_ITEMS.DATE_FROM, $event)"
                        variant="dropdown"
                        class="test-date-picker-from filter-menu-element"
                        inline
      />

      <base-date-picker v-else-if="type === FILTER_ITEMS.DATE_TO"
                        :clearable="false"
                        :value="values[FILTER_ITEMS.DATE_TO]"
                        :showDaysForOtherMonth="false"
                        @change-date="onValueChanged(FILTER_ITEMS.DATE_TO, $event)"
                        variant="dropdown"
                        class="test-date-picker-to filter-menu-element "
                        inline
      />

      <radio-dropdown v-if="type === FILTER_ITEMS.GRANULARITY"
                      :options="GRANULARITY_OPTIONS"
                      :value="values[FILTER_ITEMS.GRANULARITY]"
                      class="filter-menu-element w-max text-base"
                      @change="onValueChanged(FILTER_ITEMS.GRANULARITY, $event)"
      />

      <checkbox-dropdown v-else-if="type === FILTER_ITEMS.MARKETS"
                         :checked-values="checkedMarkets"
                         :options="marketOptions"
                         :is-no-value-allowed="true"
                         class="filter-menu-element w-max text-base"
                         @change="onValueChanged(FILTER_ITEMS.MARKETS, $event)"
      />

      <checkbox-dropdown v-else-if="type === FILTER_ITEMS.REQUEST_OUTCOME"
                         :checked-values="checkedOutcomes"
                         :options="outcomeOptions"
                         :is-no-value-allowed="true"
                         class="filter-menu-element w-max text-base"
                         @change="onValueChanged(FILTER_ITEMS.REQUEST_OUTCOME, $event)"
      />

      <checkbox-dropdown v-else-if="type === FILTER_ITEMS.ASSISTANCE_RESOLUTIONS"
                         :checked-values="checkedResolutions"
                         :options="resolutionOptions"
                         :is-no-value-allowed="true"
                         class="filter-menu-element w-max text-base"
                         @change="onValueChanged(FILTER_ITEMS.ASSISTANCE_RESOLUTIONS, $event)"
      />

      <checkbox-dropdown v-else-if="type === FILTER_ITEMS.CHANNELS"
                         :checked-values="checkedChannels"
                         :options="channelOptions"
                         :is-no-value-allowed="true"
                         class="filter-menu-element w-max text-base"
                         @change="onValueChanged(FILTER_ITEMS.CHANNELS, $event)"
      />

      <answer-select v-else-if="type === FILTER_ITEMS.ANSWER_ID"
                     ref="answerSelect"
                     :answer-options="answerList"
                     select-variant="answerSelectFilters"
                     select-variant-hidden-dropdown="answerSelectFiltersHiddenDropdown"
                     class="w-64 text-base h-15 text-gray-dark"
                     :display-clear-button="true"
                     :intent-platform-id="answerProps.intentPlatformId"
                     placeholder="Search for an Answer..."
                     @change-answer="onValueChanged(FILTER_ITEMS.ANSWER_ID, getAnswerId($event.intentPlatformId))"
                     @remove-answer="handleRemoveAnswer"
      />
    </div>

  </button>
</template>

<script>
import ListFilterValue from './ListFilterValue'
import DateFromFilterValue from './DateFromFilterValue'
import DateToFilterValue from './DateToFilterValue'
import BaseDatePicker from '@/components/base-components/BaseDatePicker'
import CheckboxDropdown from '@/components/common/CheckboxDropdown'
import SearchInput from '@/components/base-components/SearchInput'
import FilterValue from '@/components/analytics/filter/FilterValue'
import RadioDropdown from '@/components/common/RadioDropdown'
import ChartLegend from '@/components/analytics/charts/ChartLegend'
import ChannelsIcon from '@/assets/images/icons/ChannelsIcon'
import BotIcon from '@/assets/images/icons/BotIcon'
import GranularityIcon from '@/assets/images/icons/GranularityIcon'
import channelModule from '@/store/modules/channel.module'
import botModule from '@/store/modules/bot.module'
import outcomeModule from '@/store/modules/outcome.module'
import { FILTER_ITEMS, GRANULARITY_OPTIONS } from '@/utils/filter.constants'
import AnswerSelect from '@/components/analytics/conversations/detail/AnswerSelect.vue'
import answerModule from '@/store/modules/answer.module'
import ChannelTypeIcons from '@/components/analytics/filter/ChannelTypeIcons.vue'
import { getMarketsForLogicalBots, getLogicalBotOptions, getLogicalBotId } from '@/utils/market.utils'

export default {
  components: {
    AnswerSelect,
    ChannelsIcon,
    FilterValue,
    ListFilterValue,
    DateFromFilterValue,
    DateToFilterValue,
    BaseDatePicker,
    CheckboxDropdown,
    SearchInput,
    RadioDropdown,
    ChartLegend,
    BotIcon,
    GranularityIcon,
    ChannelTypeIcons
  },
  name: 'FilterMenuItem',
  props: {
    label: {
      type: String,
      required: true
    },
    isSelected: {
      type: Boolean,
      required: true
    },
    type: {
      type: String,
      required: true
    },
    values: {
      type: Object,
      required: true
    },
    useLogicalBots: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      FILTER_ITEMS,
      GRANULARITY_OPTIONS,
      showItemDropdown: true,
      logicalBotSearch: ''
    }
  },

  computed: {
    channelOptions: channelModule.getChannelList,
    botOptions: botModule.getBotList,
    outcomeOptions: outcomeModule.getOutcomeList,
    resolutionOptions: outcomeModule.getResolutionList,
    answerList: answerModule.getAnswerList,

    logicalBotOptions() {
      return getLogicalBotOptions().map(({ value, text }) => ({
        id: value,
        name: text
      }))
    },

    filteredLogicalBotOptions() {
      const term = this.logicalBotSearch.trim().toLowerCase()
      if (!term) return this.logicalBotOptions

      return this.logicalBotOptions.filter(({ name }) => (name || '').toLowerCase().includes(term))
    },

    showLogicalBotSearch() {
      return this.logicalBotOptions.length > 5
    },

    // BOTS === null means "the anchor bot only" (default)
    checkedLogicalBots() {
      const selectedBots = this.values[FILTER_ITEMS.BOTS]
      if (selectedBots !== null && selectedBots !== undefined) return selectedBots

      const anchorLogicalBotId = getLogicalBotId(this.values[FILTER_ITEMS.BOT])
      return anchorLogicalBotId ? [anchorLogicalBotId] : []
    },

    botLabels() {
      const checkedOptions = this.logicalBotOptions.filter(({ id }) => this.checkedLogicalBots.includes(id))
      if (checkedOptions.length > 1 && checkedOptions.length !== this.logicalBotOptions.length) {
        return [`${checkedOptions.length} bots`]
      }
      return checkedOptions.map(({ name }) => name)
    },

    marketOptions() {
      return getMarketsForLogicalBots(this.checkedLogicalBots)
    },

    channelLabels() {
      return this.values[FILTER_ITEMS.CHANNELS] === null ?
        this.channelOptions.map(({ name }) => name) :
        this.selectedChannels.map(({ name }) => name)
    },

    selectedChannels() {
      if (!this.values[FILTER_ITEMS.CHANNELS]) return []

      return this.channelOptions.filter(
        ({ id }) => this.values[FILTER_ITEMS.CHANNELS].includes(id)
      )
    },

    checkedChannels() {
      return this.values[FILTER_ITEMS.CHANNELS] === null ?
        this.channelOptions.map(({ id }) => id) :
        this.values[FILTER_ITEMS.CHANNELS]
    },

    marketLabels() {
      const selectedOptions = this.marketOptions.filter(({ id }) => this.checkedMarkets.includes(id))
      if (selectedOptions.length > 1 && selectedOptions.length !== this.marketOptions.length) {
        return [`${selectedOptions.length} markets`]
      }
      return selectedOptions.map(({ name }) => name)
    },

    checkedMarkets() {
      return this.values[FILTER_ITEMS.MARKETS] === null ?
        this.marketOptions.map(({ id }) => id) :
        this.values[FILTER_ITEMS.MARKETS]
    },

    outcomesLabels() {
      const selectedOptions = this.outcomeOptions.filter(
        ({ id }) => this.values[FILTER_ITEMS.REQUEST_OUTCOME]?.includes(id)
      )
      return this.values[FILTER_ITEMS.REQUEST_OUTCOME] === null ?
        this.outcomeOptions.map(({ name }) => name) :
        selectedOptions.map(({ name }) => name)
    },

    resolutionLabels() {
      const selectedOptions = this.resolutionOptions.filter(
        ({ id }) => this.values[FILTER_ITEMS.ASSISTANCE_RESOLUTIONS]?.includes(id)
      )
      return this.values[FILTER_ITEMS.ASSISTANCE_RESOLUTIONS] === null ?
        this.resolutionOptions.map(({ name }) => name) :
        selectedOptions.map(({ name }) => name)
    },

    checkedOutcomes() {
      const outcomeValues = this.values[FILTER_ITEMS.REQUEST_OUTCOME]
      return outcomeValues === null ?
        this.outcomeOptions.map(({ id }) => id) :
        outcomeValues
    },

    checkedResolutions() {
      const resolutionValues = this.values[FILTER_ITEMS.ASSISTANCE_RESOLUTIONS]
      return resolutionValues === null ?
        this.resolutionOptions.map(({ id }) => id) :
        resolutionValues
    },

    granularityLabel() {
      const { name } = GRANULARITY_OPTIONS.find(({ id }) => id === this.values[FILTER_ITEMS.GRANULARITY])
      return name
    },

    answerProps() {
      const answerId = this.values[FILTER_ITEMS.ANSWER_ID]
      if (!answerId) return { name: 'All answers', intentPlatformId: null }

      const { name, intent } = this.answerList.find(({ id }) => id === this.values[FILTER_ITEMS.ANSWER_ID])
      return { name, intentPlatformId: intent.platformId }
    },

    hideOutcomeLabels() {
      return this.checkedOutcomes.length > 1 && this.checkedOutcomes.length !== this.outcomeOptions.length
    },

    hideResolutionLabels() {
      return this.checkedResolutions.length > 1 && this.checkedResolutions.length !== this.resolutionOptions.length
    },

    hideChannelLabels() {
      return this.selectedChannels.length > 1 && this.selectedChannels.length !== this.channelOptions.length
    }
  },

  methods: {
    onLogicalBotsChanged(checkedIds) {
      this.onValueChanged(FILTER_ITEMS.BOTS, checkedIds)
    },

    onValueChanged(type, value) {
      this.$emit('change', { [type]: value })
    },

    getAnswerId(intentPlatformId) {
      return this.answerList.find(({ intent }) => intent.platformId === intentPlatformId).id
    },

    handleRemoveAnswer() {
      this.onValueChanged(FILTER_ITEMS.ANSWER_ID, null)
      this.showItemDropdown = false
    },

    async handleFilterItem() {
      this.showItemDropdown = true
      if (this.type === FILTER_ITEMS.BOT && this.useLogicalBots) {
        this.logicalBotSearch = ''
      }

      if (this.type === FILTER_ITEMS.ANSWER_ID) {
        // delay to get dropdown mounted
        await this.$nextTick()
        this.$refs.answerSelect?.focusOnAnswerSelect()
      }
    }
  }
}
</script>

<style scoped>
.dropdown {
  top: 1.25rem;
  left: 19.4rem;
}

.logical-bot-dropdown {
  min-width: 20rem;
}
</style>
